<template>
  <v-container fluid class="ma-4 mt-3 px-8">
    <!-- ── Filters ──────────────────────────────────────────────────────── -->
    <v-row justify="start" align="center">
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.exam_sitting"
          :items="examSittings"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Exam Batch"
        />
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.exam_batch"
          :items="examBatches"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Fee Raised Batch"
        />
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.program_id"
          :items="programs"
          :item-text="
            (item) =>
              `${item.degree.short_name}${
                item.specialization ? ` - ${item.specialization}` : ''
              }`
          "
          item-value="id"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Programme"
        />
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.program_scheme_id"
          :items="programSchemes"
          item-text="name"
          item-value="id"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Programme Scheme"
        />
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.year_batch"
          :items="batches"
          outlined
          dense
          hide-details="auto"
          label="Batch"
          clearable
        />
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.partner_id"
          :items="partners"
          item-text="name"
          item-value="id"
          outlined
          dense
          hide-details="auto"
          label="Partner"
          clearable
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="form.term_no"
          label="Term No"
          type="number"
          outlined
          dense
          clearable
          hide-details="auto"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model.trim="form.usn_id"
          label="USN"
          outlined
          dense
          hide-details="auto"
          clearable
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model.trim="form.course_code"
          label="Course Code"
          outlined
          dense
          hide-details="auto"
          clearable
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-autocomplete
          v-model="form.published"
          :items="[
            { name: 'Yes', id: true },
            { name: 'No', id: false },
          ]"
          outlined
          dense
          hide-details="auto"
          label="Published"
          clearable
          item-text="name"
          item-value="id"
        />
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.subject_status"
          :items="['Current', 'Arrear', 'Completed']"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Status"
        />
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.invoiced"
          :items="[
            { name: 'Yes', id: 'Y' },
            { name: 'No', id: 'N' },
          ]"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Invoiced"
          item-text="name"
          item-value="id"
        />
      </v-col>
      <v-col cols="12" md="5" class="d-flex justify-start">
        <v-btn
          class="accent white--text mr-2"
          :loading="loading"
          @click="getRows(true)"
        >
          Search
        </v-btn>
        <v-btn
          class="accent white--text mr-2"
          :disabled="!rows.length"
          :loading="downloading"
          @click="download"
        >
          <v-icon left>mdi-download</v-icon>
          Download
        </v-btn>
        <v-btn
          class="accent white--text mr-2"
          :disabled="raiseJobActive"
          @click="openOverrideDialog"
        >
          <v-icon left>mdi-swap-horizontal</v-icon>
          Change Scheme by USN
        </v-btn>
        <v-btn
          class="accent white--text mr-2"
          :disabled="raiseJobActive"
          @click="openUploadDialog"
        >
          <v-icon left>mdi-upload</v-icon>
          Upload Schedule
        </v-btn>
        <!-- <v-btn class="accent white--text mr-2" @click="openRaiseDialog">
          <v-icon left>mdi-file-document-multiple-outline</v-icon>
          Raise Exam Papers
        </v-btn>
        <v-btn class="accent white--text mr-2" @click="openRaiseInvoiceDialog">
          <v-icon left>mdi-receipt-text-plus-outline</v-icon>
          Raise Invoice
        </v-btn> -->
      </v-col>
    </v-row>

    <!-- A Raise Papers / Raise Invoice job rewrites exam_papers in the background, so
         editing papers is paused until it finishes. -->
    <v-alert
      v-if="raiseJobActive"
      type="info"
      outlined
      dense
      class="text-body-2 mt-3 mb-0"
    >
      {{ raiseActiveText }} Editing exam papers is paused until it finishes.
      <v-progress-linear indeterminate color="accent" class="mt-2" />
    </v-alert>

    <!-- ── Table ────────────────────────────────────────────────────────── -->
    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="headers"
            :items="rows"
            :server-items-length="totalRecords"
            :options.sync="tableOptions"
            :footer-props="{ 'items-per-page-options': [10, 50, 100] }"
            fixed-header
            class="secondary row-height-50"
            :loading="loading"
            @update:options="onTableOptionsChange"
          >
            <template #item.subject_status="{ item }">
              <v-chip
                x-small
                :color="item.subject_status === 'Arrear' ? 'error' : 'success'"
                dark
              >
                {{ item.subject_status }}
              </v-chip>
            </template>

            <template #item.credits="{ item }">
              {{ item.credits ?? "-" }}
            </template>

            <template #item.amount="{ item }">
              {{ item.amount ? item.amount : "-" }}
            </template>

            <template #item.invoiced="{ item }">
              <v-chip
                x-small
                :color="item.invoiced === 'Y' ? 'success' : 'grey lighten-1'"
                dark
              >
                {{ item.invoiced }}
              </v-chip>
            </template>

            <template #item.course_name="{ item }">
              <span v-if="item.course_name">{{ item.course_name }}</span>
              <span v-else class="grey--text text-caption">
                Not chosen --
                {{ (item.available_courses || []).length }} option{{
                  (item.available_courses || []).length === 1 ? "" : "s"
                }}
              </span>
            </template>

            <template #item.actions="{ item }">
              <v-btn
                icon
                small
                :disabled="raiseJobActive"
                @click="openEditDialog(item)"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </template>

            <template #no-data>
              <div class="text-center grey--text py-4">
                No papers found. Apply filters and click Search.
              </div>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>

    <!-- ── Edit course dialog ──────────────────────────────────────────── -->
    <v-dialog v-model="editDialog" max-width="580" persistent>
      <v-card v-if="editingRow">
        <v-card-title class="font-weight-medium secondary--text primary">
          Change Course
          <v-spacer />
          <v-btn small icon @click="closeEditDialog">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <div class="text-caption grey--text mb-3">
            {{ editingRow.usn_id }} -- {{ editingRow.name }} -- Term
            {{ editingRow.term_no }} -- {{ editingRow.type }}
          </div>
          <v-autocomplete
            v-model="editCourseId"
            :items="editingRow.available_courses"
            :item-text="(c) => `${c.code} - ${c.name}`"
            item-value="id"
            outlined
            dense
            hide-details="auto"
            label="Course"
          />
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn text @click="closeEditDialog">Cancel</v-btn>
          <v-btn
            class="accent white--text"
            :loading="editSaving"
            :disabled="!editCourseId"
            @click="saveEdit"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Change scheme by USN dialog ─────────────────────────────────── -->
    <v-dialog v-model="overrideDialog" max-width="480" persistent>
      <v-card>
        <v-card-title class="font-weight-medium secondary--text primary">
          Change Scheme by USN
          <v-spacer />
          <v-btn small icon @click="closeOverrideDialog">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-text-field
            v-model.trim="overrideForm.usn_id"
            label="USN*"
            outlined
            dense
            hide-details="auto"
            class="mb-4"
          />
          <v-autocomplete
            v-model="overrideForm.program_scheme_id"
            :items="programSchemes"
            item-text="name"
            item-value="id"
            outlined
            dense
            hide-details="auto"
            label="New Scheme*"
          />
          <div v-if="overrideResult" class="text-caption mt-4">
            <div class="success--text">
              Scheme updated to {{ overrideResult.program_scheme_name }}.
            </div>
            <div
              v-if="overrideResult.kept_existing_count > 0"
              class="warning--text mt-1"
            >
              {{ overrideResult.kept_existing_count }} existing row(s) for this
              student's current term already had a result or marks entered and
              were preserved, not replaced.
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer />
          <v-btn text @click="closeOverrideDialog">Close</v-btn>
          <v-btn
            class="accent white--text"
            :loading="overrideSaving"
            :disabled="!overrideForm.usn_id || !overrideForm.program_scheme_id"
            @click="confirmOverride"
          >
            Apply
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Upload schedule dialog ───────────────────────────────────────── -->
    <v-dialog v-model="uploadDialog" max-width="520" persistent scrollable>
      <v-card>
        <v-card-title class="font-weight-medium secondary--text primary">
          Upload Exam Schedule
          <v-spacer />
          <v-btn small icon @click="closeUploadDialog">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <div class="text-caption grey--text mb-3">
            Download the csv, fill in "Exam Date" (DD-MM-YYYY) and/or "Exam
            Slot", and upload it back here. Blank cells are left untouched.
          </div>
          <v-file-input
            v-model="uploadForm.file"
            label="Select filled CSV"
            outlined
            dense
            accept=".csv"
            hide-details="auto"
            prepend-inner-icon="mdi-file-upload"
            prepend-icon=""
          >
            <template v-slot:selection="{ text }">
              <span class="text-body-2 text-truncate" style="max-width: 100%">
                {{ uploadForm.file ? uploadForm.file.name : text }}
              </span>
            </template>
          </v-file-input>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn
            color="primary"
            :disabled="!uploadForm.file"
            :loading="uploading"
            @click="submitUploadSchedule"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Raise invoice dialog ─────────────────────────────────────────── -->
    <v-dialog
      v-model="raiseInvoiceDialog"
      max-width="560"
      persistent
      scrollable
    >
      <v-card>
        <v-card-title class="font-weight-medium secondary--text primary">
          Raise Invoice
          <v-spacer />
          <v-btn small icon @click="closeRaiseInvoiceDialog">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <div class="text-caption grey--text mb-4">
            Raise invoices only after all exam papers are raised, the amounts
            are verified and the elective configuration is published. Students
            who already have an invoice for the selected batch are skipped.
            Papers with a ₹0 fee get no invoice; their marks entries are opened.
          </div>

          <div v-if="raiseStatusLoading" class="text-center py-4">
            <v-progress-circular indeterminate color="accent" />
            <div class="text-caption grey--text mt-2">
              Checking for running jobs...
            </div>
          </div>

          <v-alert
            v-else-if="raiseStatusFailed"
            type="error"
            outlined
            dense
            class="text-body-2 mb-0"
          >
            Could not check whether a raise is running. Close this window and
            try again.
          </v-alert>

          <div v-else-if="raiseJobActive">
            <div class="text-body-2 mb-2">
              {{ raiseActiveText }} Invoices can be raised after it finishes.
              You can close this window and check back later.
            </div>
            <v-progress-linear indeterminate color="accent" />
          </div>

          <template v-else>
            <v-autocomplete
              v-model="raiseInvoiceForm.exam_batch"
              :items="examBatches"
              outlined
              dense
              hide-details="auto"
              label="Fee Raised Batch*"
              class="mb-4"
            />
            <v-autocomplete
              v-model="raiseInvoiceForm.program_id"
              :items="programs"
              :item-text="
                (item) =>
                  `${item.degree.short_name}${
                    item.specialization ? ` - ${item.specialization}` : ''
                  }`
              "
              item-value="id"
              outlined
              dense
              clearable
              hide-details="auto"
              label="Programme"
              class="mb-4"
            />
            <v-autocomplete
              v-model="raiseInvoiceForm.year_batch"
              :items="batches"
              outlined
              dense
              clearable
              hide-details="auto"
              label="Batch"
              class="mb-2"
            />

            <v-radio-group
              v-model="raiseInvoiceForm.usn_mode"
              row
              dense
              hide-details
              class="mt-2 mb-2"
            >
              <v-radio label="Include USNs" value="include" />
              <v-radio label="Exclude USNs" value="exclude" />
            </v-radio-group>
            <v-combobox
              v-model="raiseInvoiceForm.usn_ids"
              multiple
              small-chips
              deletable-chips
              clearable
              outlined
              dense
              hide-details="auto"
              append-icon=""
              :label="
                raiseInvoiceForm.usn_mode === 'exclude'
                  ? 'USNs to exclude'
                  : 'USNs to include'
              "
              hint="Type or paste USNs (comma, space or new line separated)"
              persistent-hint
              @change="splitPastedUsns"
            />

            <v-alert
              v-if="raiseInvoicePreview"
              dense
              outlined
              class="text-body-2 mt-4 mb-0"
              :type="raiseInvoicePreview.nothing_to_raise ? 'error' : 'warning'"
            >
              <div v-if="raiseInvoicePreview.nothing_to_raise">
                <template v-if="raiseInvoicePreview.already_invoiced">
                  All students in
                  <strong>{{ raiseInvoicePreview.exam_batch }}</strong> for the
                  selected filters are already invoiced. Nothing to raise.
                </template>
                <template v-else>
                  No exam papers in
                  <strong>{{ raiseInvoicePreview.exam_batch }}</strong> for the
                  selected filters are waiting for an invoice.
                </template>
              </div>
              <template v-else>
                <div>
                  <strong>{{ raiseInvoicePreview.exam_batch }}</strong
                  >: {{ raiseInvoicePreview.to_invoice }} student(s) will be
                  invoiced.
                </div>
                <div v-if="raiseInvoicePreview.zero_fee">
                  {{ raiseInvoicePreview.zero_fee }} student(s) have only ₹0
                  papers: no invoice, marks will be opened.
                </div>
                <div v-if="raiseInvoicePreview.already_invoiced">
                  {{ raiseInvoicePreview.already_invoiced }} student(s) are
                  already invoiced and will be skipped.
                </div>
                <div v-if="raiseInvoicePreview.missing_fee">
                  {{ raiseInvoicePreview.missing_fee }} student(s) have a paper
                  with no fee yet: filled if a fee is configured, otherwise
                  skipped.
                </div>
                <div class="mt-1">Do you want to continue?</div>
              </template>
            </v-alert>

            <v-alert
              v-else-if="raiseInvoiceRun"
              dense
              outlined
              class="mt-4 mb-0"
              :type="raiseRunAlertType(raiseInvoiceRun)"
            >
              <div v-if="raiseInvoiceRun.state === 'completed'">
                <div>
                  {{ raiseInvoiceRun.exam_batch }}:
                  {{ raiseInvoiceResult.invoices_created || 0 }} invoice(s)
                  raised, {{ raiseInvoiceResult.zero_fee_processed || 0 }} ₹0
                  paper(s) processed, {{ raiseInvoiceResult.fees_filled || 0 }}
                  missing fee(s) filled.
                </div>
                <div
                  v-if="
                    (raiseInvoiceResult.skipped_existing_invoice || []).length
                  "
                >
                  Skipped -- already invoiced:
                  {{ raiseInvoiceResult.skipped_existing_invoice.length }}
                  student(s).
                </div>
                <div
                  v-if="(raiseInvoiceResult.skipped_missing_fee || []).length"
                >
                  Skipped -- fee missing (no invoice raised):
                  {{
                    raiseInvoiceResult.skipped_missing_fee
                      .map((s) => s.usn_id)
                      .join(", ")
                  }}
                </div>
              </div>
              <div v-else>
                The last run did not finish ({{
                  raiseInvoiceResult.error || raiseInvoiceRun.state
                }}). Please contact the technical team before raising again.
              </div>
            </v-alert>
          </template>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-3">
          <v-spacer />
          <v-btn text @click="closeRaiseInvoiceDialog">Close</v-btn>
          <template v-if="raiseFormVisible">
            <v-btn
              v-if="raiseInvoicePreview"
              text
              @click="raiseInvoicePreview = null"
            >
              Back
            </v-btn>
            <v-btn
              v-if="!raiseInvoicePreview"
              color="primary"
              :disabled="!raiseInvoiceForm.exam_batch"
              :loading="raiseInvoicePreviewing"
              @click="previewRaiseInvoice"
            >
              Raise Invoice
            </v-btn>
            <v-btn
              v-else-if="!raiseInvoicePreview.nothing_to_raise"
              color="primary"
              :loading="raiseInvoiceSubmitting"
              @click="submitRaiseInvoice"
            >
              Yes, Raise
            </v-btn>
          </template>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Raise exam papers dialog ─────────────────────────────────────── -->
    <v-dialog v-model="raiseDialog" max-width="560" persistent scrollable>
      <v-card>
        <v-card-title class="font-weight-medium secondary--text primary">
          Raise Exam Papers
          <v-spacer />
          <v-btn small icon @click="closeRaiseDialog">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-alert type="info" outlined dense class="text-body-2">
            Raising exam papers can take 10 minutes or more. Once started,
            please wait for it to finish before starting another one. You can
            close this window and check back later.
          </v-alert>

          <div v-if="raiseStatusLoading" class="text-center py-4">
            <v-progress-circular indeterminate color="accent" />
            <div class="text-caption grey--text mt-2">
              Checking for running jobs...
            </div>
          </div>

          <v-alert
            v-else-if="raiseStatusFailed"
            type="error"
            outlined
            dense
            class="text-body-2 mb-0"
          >
            Could not check whether a raise is running. Close this window and
            try again.
          </v-alert>

          <div v-else-if="raiseJobActive">
            <div class="text-body-2 mb-2">
              {{ raiseActiveText }} Please wait...
            </div>
            <v-progress-linear indeterminate color="accent" />
          </div>

          <!-- Last run -->
          <div v-else-if="raiseLastRun" class="mb-4">
            <v-alert
              v-if="raiseLastRun.state === 'completed'"
              type="success"
              outlined
              dense
              class="text-body-2 mb-0"
            >
              Last raise: <strong>{{ raiseLastRun.exam_batch }}</strong> ({{
                raiseRunLabel(raiseLastRun)
              }}) completed. {{ raiseResult.fees_updated || 0 }} paper fee(s)
              set. Raise invoices with "Raise Invoice" after electives are
              published.
              <div v-if="raiseNoFeeUsns.length" class="warning--text mt-1">
                Fee is not configured for
                {{ raiseNoFeeUsns.length }} student(s):
                {{ raiseNoFeeUsns.slice(0, 10).join(", ")
                }}{{ raiseNoFeeUsns.length > 10 ? "..." : "" }}
              </div>
            </v-alert>
            <v-alert
              v-else
              type="error"
              outlined
              dense
              class="text-body-2 mb-0"
            >
              The last raise for
              <strong>{{ raiseLastRun.exam_batch }}</strong> did not complete.
              Please contact the technical team before raising again.
            </v-alert>
          </div>

          <template v-if="raiseFormVisible">
            <v-checkbox
              v-model="raiseForm.freshers"
              label="Freshers"
              hide-details
              dense
              class="mt-0 mb-4"
            />
            <v-autocomplete
              v-if="raiseForm.freshers"
              v-model="raiseForm.year_batch"
              :items="batches"
              outlined
              dense
              hide-details="auto"
              label="Freshers Batch*"
              class="mb-4"
            />
            <v-autocomplete
              v-if="!raiseForm.freshers"
              v-model="raiseForm.duration_type"
              :items="programTypes"
              outlined
              dense
              hide-details="auto"
              label="Programme Type*"
              class="mb-4"
            />
            <v-row dense>
              <v-col cols="6">
                <v-autocomplete
                  v-model="raiseForm.raised_month"
                  :items="months"
                  outlined
                  dense
                  hide-details="auto"
                  label="Fee Raised Month*"
                />
              </v-col>
              <v-col cols="6">
                <v-autocomplete
                  v-model="raiseForm.raised_year"
                  :items="raiseYears"
                  outlined
                  dense
                  hide-details="auto"
                  label="Fee Raised Year*"
                />
              </v-col>
              <v-col cols="6">
                <v-autocomplete
                  v-model="raiseForm.exam_month"
                  :items="months"
                  outlined
                  dense
                  hide-details="auto"
                  label="Exam Month*"
                />
              </v-col>
              <v-col cols="6">
                <v-autocomplete
                  v-model="raiseForm.exam_year"
                  :items="raiseYears"
                  outlined
                  dense
                  hide-details="auto"
                  label="Exam Year*"
                />
              </v-col>
            </v-row>
            <div v-if="raiseBatchLabel" class="text-caption grey--text mt-2">
              Fee Raised Batch: <strong>{{ raiseBatchLabel }}</strong>
            </div>

            <v-alert
              v-if="raiseConfirming && raiseExisting"
              type="error"
              outlined
              dense
              class="text-body-2 mt-4 mb-0"
            >
              Exam papers for <strong>{{ raiseExisting.exam_batch }}</strong>
              have already been raised:
              {{ raiseExisting.exam_papers }} exam paper(s)<template
                v-if="raiseExisting.invoices"
                >, {{ raiseExisting.invoices }} exam fee invoice(s) already
                raised</template
              >. Raise again only if this is a different group, e.g. freshers
              after semester students. Raise
              <strong>{{ raiseScopeText }}</strong> again?
            </v-alert>
            <v-alert
              v-else-if="raiseConfirming"
              type="warning"
              outlined
              dense
              class="text-body-2 mt-4 mb-0"
            >
              This will raise <strong>{{ raiseBatchLabel }}</strong> exam papers
              for <strong>{{ raiseScopeText }}</strong> (no invoices yet), exam
              in <strong>{{ raiseExamText }}</strong
              >. Do you want to continue?
            </v-alert>
          </template>
        </v-card-text>
        <v-divider />
        <v-card-actions class="pa-3">
          <v-spacer />
          <template v-if="raiseFormVisible">
            <v-btn v-if="raiseConfirming" text @click="cancelRaiseConfirm">
              Back
            </v-btn>
            <v-btn
              v-if="!raiseConfirming"
              color="primary"
              :disabled="!raiseFormValid"
              @click="raiseConfirming = true"
            >
              Raise
            </v-btn>
            <v-btn
              v-else-if="raiseExisting"
              color="error"
              :loading="raiseSubmitting"
              @click="submitRaise(true)"
            >
              Yes, Raise Again
            </v-btn>
            <v-btn
              v-else
              color="primary"
              :loading="raiseSubmitting"
              @click="submitRaise(false)"
            >
              Yes, Raise
            </v-btn>
          </template>
          <v-btn v-else text @click="closeRaiseDialog">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { examPaperHeaders } from "@/constants/headers";

export default {
  name: "ExamPapersView",

  data() {
    return {
      form: {
        program_id: null,
        program_scheme_id: null,
        year_batch: null,
        partner_id: null,
        term_no: null,
        usn_id: "",
        course_code: "",
        type: null,
        subject_status: null,
        published: null,
        exam_batch: null,
        exam_sitting: null,
        invoiced: null,
      },
      programs: [],
      programSchemes: [],
      batches: [],
      partners: [],
      rows: [],
      totalRecords: 0,
      loading: false,
      downloading: false,
      isResetting: false,
      tableOptions: { page: 1, itemsPerPage: 10 },
      headers: examPaperHeaders,
      editDialog: false,
      editingRow: null,
      editCourseId: null,
      editSaving: false,
      overrideDialog: false,
      overrideForm: { usn_id: "", program_scheme_id: null },
      overrideResult: null,
      overrideSaving: false,
      examBatches: [],
      examSittings: [],
      uploadDialog: false,
      uploadForm: { file: null },
      uploading: false,
      downloadingSchedule: false,
      raiseInvoiceDialog: false,
      raiseInvoiceForm: {
        exam_batch: null,
        program_id: null,
        year_batch: null,
        usn_mode: "include",
        usn_ids: [],
      },
      raiseInvoiceSubmitting: false,
      raiseInvoicePreview: null,
      raiseInvoicePreviewing: false,
      // /exam_paper/raise_status -- shared by both dialogs and the page banner.
      raiseStatus: null,
      raiseStatusLoading: false,
      raiseStatusFailed: false,
      raiseStatusPoll: null,
      raiseDialog: false,
      raiseForm: {
        freshers: false,
        year_batch: null,
        duration_type: null,
        raised_month: null,
        raised_year: null,
        exam_month: null,
        exam_year: null,
      },
      raiseConfirming: false,
      raiseSubmitting: false,
      raiseExisting: null,
      programTypes: [
        { text: "Semester", value: "S" },
        { text: "Annual", value: "Y" },
      ],
      months: [
        { text: "January", value: "01", short: "Jan" },
        { text: "February", value: "02", short: "Feb" },
        { text: "March", value: "03", short: "Mar" },
        { text: "April", value: "04", short: "Apr" },
        { text: "May", value: "05", short: "May" },
        { text: "June", value: "06", short: "Jun" },
        { text: "July", value: "07", short: "Jul" },
        { text: "August", value: "08", short: "Aug" },
        { text: "September", value: "09", short: "Sep" },
        { text: "October", value: "10", short: "Oct" },
        { text: "November", value: "11", short: "Nov" },
        { text: "December", value: "12", short: "Dec" },
      ],
    };
  },

  computed: {
    // Latest run of either action -- the server runs one at a time, so this is the one
    // that blocks both dialogs.
    raiseCurrentRun() {
      return (this.raiseStatus && this.raiseStatus.last_run) || null;
    },
    raiseLastRun() {
      return (this.raiseStatus && this.raiseStatus.last_papers_run) || null;
    },
    raiseInvoiceRun() {
      return (this.raiseStatus && this.raiseStatus.last_invoice_run) || null;
    },
    raiseJobActive() {
      return (
        !!this.raiseCurrentRun &&
        ["queued", "running"].includes(this.raiseCurrentRun.state)
      );
    },
    raiseActiveText() {
      const run = this.raiseCurrentRun;
      if (!run) return "";
      return run.action === "invoice"
        ? `Invoices for ${run.exam_batch} are being raised.`
        : `Exam papers for ${run.exam_batch} are being raised.`;
    },
    // Forms show only once the server has confirmed nothing is running.
    raiseFormVisible() {
      return (
        !this.raiseStatusLoading &&
        !this.raiseStatusFailed &&
        !this.raiseJobActive
      );
    },
    raiseInvoiceResult() {
      return (this.raiseInvoiceRun && this.raiseInvoiceRun.result) || {};
    },
    raiseYears() {
      const year = new Date().getFullYear();
      return [year - 1, year, year + 1, year + 2];
    },

    raiseResult() {
      return (this.raiseLastRun && this.raiseLastRun.result) || {};
    },

    raiseNoFeeUsns() {
      return this.raiseResult.no_fee_usns || [];
    },

    raiseBatchLabel() {
      const { raised_month, raised_year } = this.raiseForm;
      if (!raised_month || !raised_year) return "";
      const month = this.months.find((m) => m.value === raised_month);
      return `${month.short}-${String(raised_year).slice(-2)}`;
    },

    raiseExamText() {
      const { exam_month, exam_year } = this.raiseForm;
      const month = this.months.find((m) => m.value === exam_month);
      return month && exam_year ? `${month.text} ${exam_year}` : "";
    },

    raiseScopeText() {
      if (this.raiseForm.freshers) {
        return `freshers of batch ${this.raiseForm.year_batch}`;
      }
      return this.raiseForm.duration_type === "Y"
        ? "all annual programme students"
        : "all semester programme students";
    },

    raiseFormValid() {
      const f = this.raiseForm;
      return (
        (f.freshers ? !!f.year_batch : !!f.duration_type) &&
        !!f.raised_month &&
        !!f.raised_year &&
        !!f.exam_month &&
        !!f.exam_year
      );
    },
  },

  watch: {
    // A changed form invalidates the confirm step's preview / "already raised" warning.
    raiseInvoiceForm: {
      deep: true,
      handler() {
        this.raiseInvoicePreview = null;
      },
    },
    raiseForm: {
      deep: true,
      handler() {
        this.raiseExisting = null;
      },
    },
  },

  created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Exam Papers" }]);
    this.init();
    // Picks up a run started earlier (or by another admin) for the page banner.
    this.fetchRaiseStatus();
  },

  beforeDestroy() {
    this.stopRaiseStatusPoll();
  },

  methods: {
    objectToFilteredParams(obj) {
      const newObj = { ...obj };
      if (newObj.year_batch) {
        const [year, batch] = newObj.year_batch.split("-");
        newObj.year = year;
        newObj.batch = batch;
        delete newObj.year_batch;
      }
      return Object.keys(newObj)
        .filter(
          (key) =>
            newObj[key] !== undefined &&
            newObj[key] !== null &&
            newObj[key] !== "",
        )
        .reduce((acc, key) => {
          acc[key] = newObj[key];
          return acc;
        }, {});
    },

    async init() {
      await Promise.all([
        this.fetchProgrammes(),
        this.fetchProgramSchemes(),
        this.fetchBatches(),
        this.fetchPartners(),
        this.fetchExamBatches(),
        this.fetchExamSittings(),
      ]);
    },

    onTableOptionsChange(options) {
      if (this.isResetting) return;
      this.tableOptions = options;
      this.getRows();
    },

    async getRows(resetPage = false) {
      if (resetPage) {
        this.isResetting = true;
        this.tableOptions = { ...this.tableOptions, page: 1 };
        this.$nextTick(() => {
          this.isResetting = false;
        });
      }

      this.loading = true;
      const params = this.objectToFilteredParams({ ...this.form });

      try {
        const res = await this.$axios.get("/exam_paper_rows", {
          params: {
            ...params,
            page: this.tableOptions.page,
            per_page: this.tableOptions.itemsPerPage,
          },
        });
        this.rows = res.data.data.records;
        this.totalRecords = res.data.data.total;
      } catch (err) {
        this.$store.commit("showSnackbar", {
          message: err.response?.data?.message || "Failed to load papers",
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },

    async fetchProgrammes() {
      try {
        const res = await this.$axios.get("/programs");
        this.programs = res.data.data || [];
      } catch (err) {
        console.error(err);
      }
    },

    async fetchProgramSchemes() {
      try {
        const res = await this.$axios.get("/program_schemes");
        this.programSchemes = res.data.data || [];
      } catch (err) {
        console.error(err);
      }
    },

    async fetchBatches() {
      try {
        const res = await this.$axios.get("/get_year_batch");
        this.batches = res.data.data || [];
      } catch (err) {
        console.error(err);
      }
    },

    async fetchPartners() {
      try {
        const res = await this.$axios.get("/partners");
        this.partners = res.data.data || [];
      } catch (err) {
        console.error(err);
      }
    },
    // "Fee Raised Batch" is exam_papers.exam_batch, the billing-cycle label (same value as
    // the invoice's raised_batch). "Exam Batch" is exam_year/exam_month (e.g. "2027-Feb"),
    // the same value Student Exam Result calls its batch.
    async fetchExamBatches() {
      try {
        const res = await this.$axios.get("/exam_paper/exam_batches");
        this.examBatches = res.data.data || [];
      } catch (err) {
        this.$store.commit("showSnackbar", {
          message:
            err.response?.data?.message || "Failed to load exam batches",
          color: "error",
        });
      }
    },

    async fetchExamSittings() {
      try {
        const res = await this.$axios.get("/exam_paper/exam_sittings");
        this.examSittings = res.data.data || [];
      } catch (err) {
        this.$store.commit("showSnackbar", {
          message:
            err.response?.data?.message || "Failed to load exam batches",
          color: "error",
        });
      }
    },

    async download() {
      const params = this.objectToFilteredParams({ ...this.form });
      this.downloading = true;

      this.$axios
        .get("/exam_paper/download_schedule", { responseType: "blob", params })
        .then((res) => {
          const url = window.URL.createObjectURL(
            new Blob([res.data], { type: "text/csv" }),
          );
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "Exam Papers.csv");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch((err) => {
          this.$store.commit("showSnackbar", {
            message: err.response?.data?.message || "Download failed",
            color: "error",
          });
        })
        .finally(() => {
          this.downloading = false;
        });
    },

    openEditDialog(row) {
      this.editingRow = row;
      this.editCourseId = row.course_id;
      this.editDialog = true;
    },

    closeEditDialog() {
      this.editDialog = false;
      this.editingRow = null;
      this.editCourseId = null;
    },

    async saveEdit() {
      if (!this.editingRow || !this.editCourseId) return;

      this.editSaving = true;
      try {
        // Saves on the exam_papers row; refused once the term's electives are published.
        await this.$axios.put("/admin_student_electives/upsert", {
          student_elective: {
            program_scheme_term_course_id:
              this.editingRow.program_scheme_term_course_id,
            student_detail_id: this.editingRow.student_detail_id,
            course_id: this.editCourseId,
          },
        });
        this.$store.commit("showSnackbar", {
          message: "Course updated",
          color: "success",
        });
        this.closeEditDialog();
        this.getRows();
      } catch (err) {
        this.$store.commit("showSnackbar", {
          message: err.response?.data?.message || "Update failed",
          color: "error",
        });
      } finally {
        this.editSaving = false;
      }
    },

    openOverrideDialog() {
      this.overrideForm = { usn_id: "", program_scheme_id: null };
      this.overrideResult = null;
      this.overrideDialog = true;
    },

    closeOverrideDialog() {
      this.overrideDialog = false;
      this.overrideForm = { usn_id: "", program_scheme_id: null };
      this.overrideResult = null;
    },

    async confirmOverride() {
      this.overrideSaving = true;
      this.overrideResult = null;

      try {
        const res = await this.$axios.put("/exam_paper/override_scheme", {
          usn_id: this.overrideForm.usn_id,
          program_scheme_id: this.overrideForm.program_scheme_id,
        });
        this.overrideResult = res.data.data;
        this.getRows();
      } catch (err) {
        this.$store.commit("showSnackbar", {
          message: err.response?.data?.message || "Scheme change failed",
          color: "error",
        });
      } finally {
        this.overrideSaving = false;
      }
    },

    openUploadDialog() {
      this.uploadForm = { file: null };
      this.uploadDialog = true;
    },

    closeUploadDialog() {
      this.uploadDialog = false;
      this.uploadForm = { file: null };
    },

    openRaiseDialog() {
      this.raiseForm = {
        freshers: false,
        year_batch: null,
        duration_type: null,
        raised_month: null,
        raised_year: null,
        exam_month: null,
        exam_year: null,
      };
      this.raiseConfirming = false;
      this.raiseExisting = null;
      this.raiseDialog = true;
      this.loadRaiseStatus();
    },

    closeRaiseDialog() {
      this.raiseDialog = false;
      this.raiseConfirming = false;
      this.raiseExisting = null;
    },

    cancelRaiseConfirm() {
      this.raiseConfirming = false;
      this.raiseExisting = null;
    },

    raiseRunLabel(run) {
      if (run.freshers) {
        return `Freshers ${run.admitted_year}-${run.admitted_batch}`;
      }
      return run.duration_type === "Y" ? "Annual" : "Semester";
    },

    // On dialog open: the form stays hidden until the server says nothing is running.
    async loadRaiseStatus() {
      this.raiseStatusLoading = true;
      this.raiseStatusFailed = false;
      await this.fetchRaiseStatus();
      this.raiseStatusLoading = false;
    },

    async fetchRaiseStatus() {
      try {
        const res = await this.$axios.get("/exam_paper/raise_status");
        const wasActive = this.raiseJobActive;
        this.raiseStatus = res.data.data || {};
        this.raiseStatusFailed = false;

        if (this.raiseJobActive) {
          this.startRaiseStatusPoll();
        } else {
          this.stopRaiseStatusPoll();
          // A run just finished: new batches may exist and rows have changed.
          if (wasActive) {
            this.fetchExamBatches();
            this.fetchExamSittings();
            if (this.rows.length) this.getRows();
          }
        }
      } catch (err) {
        this.raiseStatusFailed = true;
        this.stopRaiseStatusPoll();
        this.$store.commit("showSnackbar", {
          message: err.response?.data?.message || "Failed to load raise status",
          color: "error",
        });
      }
    },

    // Runs while a job is active, dialog open or not, so the page banner clears itself.
    startRaiseStatusPoll() {
      if (this.raiseStatusPoll) return;
      this.raiseStatusPoll = setInterval(this.fetchRaiseStatus, 10000);
    },

    stopRaiseStatusPoll() {
      if (this.raiseStatusPoll) clearInterval(this.raiseStatusPoll);
      this.raiseStatusPoll = null;
    },

    // A just-started run, shown at once without waiting for the next status poll.
    applyStartedRun(run) {
      const key =
        run.action === "invoice" ? "last_invoice_run" : "last_papers_run";
      this.raiseStatus = { ...this.raiseStatus, last_run: run, [key]: run };
      this.startRaiseStatusPoll();
    },

    // confirmExisting: the admin saw the "already raised" warning and chose to go on.
    async submitRaise(confirmExisting) {
      this.raiseSubmitting = true;
      const f = this.raiseForm;
      const payload = {
        freshers: f.freshers,
        raised_month: f.raised_month,
        raised_year: f.raised_year,
        exam_month: f.exam_month,
        exam_year: f.exam_year,
        confirm_existing: !!confirmExisting,
      };
      if (f.freshers) payload.year_batch = f.year_batch;
      else payload.duration_type = f.duration_type;

      try {
        const res = await this.$axios.post("/exam_paper/raise", payload);
        this.raiseConfirming = false;
        this.raiseExisting = null;
        this.applyStartedRun(res.data.data);
        this.$store.commit("showSnackbar", {
          message: `Raising ${this.raiseBatchLabel} exam papers has started`,
          color: "success",
        });
      } catch (err) {
        if (err.response?.data?.code === "already_raised") {
          this.raiseExisting = err.response.data.data;
          return;
        }
        this.$store.commit("showSnackbar", {
          message:
            err.response?.data?.message ||
            "Could not start raising exam papers",
          color: "error",
        });
        if (err.response?.status === 409) this.fetchRaiseStatus();
      } finally {
        this.raiseSubmitting = false;
      }
    },

    async submitUploadSchedule() {
      if (!this.uploadForm.file) return;
      this.uploading = true;

      const formData = new FormData();
      formData.append("file", this.uploadForm.file, this.uploadForm.file.name);

      await this.$axios
        .post("/exam_paper/upload_schedule", formData, {
          headers: { "Content-Type": "multipart/form-data" },
          responseType: "blob",
          validateStatus: (s) => s < 500,
        })
        .then((res) => {
          // 4xx = validation error (e.g. bad CSV headers) -- parse JSON from blob
          if (res.status >= 400) {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const data = JSON.parse(reader.result);
                this.$store.commit("showSnackbar", {
                  message: data.message || "Upload failed",
                  color: "error",
                });
              } catch {
                this.$store.commit("showSnackbar", {
                  message: "Upload failed",
                  color: "error",
                });
              }
            };
            reader.readAsText(res.data);
            return;
          }

          // 201 = row-level errors -- download error CSV
          if (res.status === 201) {
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "exam_schedule_errors.csv");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            return;
          }

          // 200 = success
          this.$store.commit("showSnackbar", {
            message: "Exam schedule updated successfully",
            color: "success",
          });
          this.closeUploadDialog();
          this.getRows();
        })
        .catch((err) => {
          this.$store.commit("showSnackbar", {
            message: err.response?.data?.message || "Upload failed",
            color: "error",
          });
        })
        .finally(() => {
          this.uploading = false;
        });
    },

    openRaiseInvoiceDialog() {
      this.raiseInvoiceForm = {
        exam_batch: this.form.exam_batch || null,
        program_id: null,
        year_batch: null,
        usn_mode: "include",
        usn_ids: [],
      };
      this.raiseInvoicePreview = null;
      this.raiseInvoiceDialog = true;
      this.loadRaiseStatus();
    },

    closeRaiseInvoiceDialog() {
      this.raiseInvoiceDialog = false;
      this.raiseInvoicePreview = null;
    },

    // A pasted list arrives as one chip -- split it into one chip per USN.
    splitPastedUsns(values) {
      this.raiseInvoiceForm.usn_ids = [
        ...new Set(
          (values || [])
            .flatMap((v) => String(v).split(/[\s,;]+/))
            .map((v) => v.trim().toUpperCase())
            .filter(Boolean),
        ),
      ];
    },

    raiseRunAlertType(run) {
      if (["queued", "running"].includes(run.state)) return "info";
      return run.state === "completed" ? "success" : "error";
    },

    raiseInvoicePayload() {
      const f = this.raiseInvoiceForm;
      return {
        exam_batch: f.exam_batch,
        program_id: f.program_id,
        year_batch: f.year_batch,
        usn_mode: f.usn_ids.length ? f.usn_mode : null,
        usn_ids: f.usn_ids,
      };
    },

    // Confirm step: what the run would do (or that there's nothing to raise), before
    // anything is queued.
    async previewRaiseInvoice() {
      this.raiseInvoicePreviewing = true;

      try {
        const res = await this.$axios.get("/exam_paper/raise_invoice_preview", {
          params: this.raiseInvoicePayload(),
        });
        this.raiseInvoicePreview = res.data.data;
      } catch (err) {
        this.$store.commit("showSnackbar", {
          message: err.response?.data?.message || "Could not check invoices",
          color: "error",
        });
      } finally {
        this.raiseInvoicePreviewing = false;
      }
    },

    async submitRaiseInvoice() {
      this.raiseInvoiceSubmitting = true;

      try {
        const res = await this.$axios.post(
          "/exam_paper/raise_invoice",
          this.raiseInvoicePayload(),
        );
        this.raiseInvoicePreview = null;
        this.applyStartedRun(res.data.data);
        this.$store.commit("showSnackbar", {
          message: `Raising ${res.data.data.exam_batch} invoices has started`,
          color: "success",
        });
      } catch (err) {
        this.$store.commit("showSnackbar", {
          message:
            err.response?.data?.message || "Could not start raising invoices",
          color: "error",
        });
        if (err.response?.status === 409) this.fetchRaiseStatus();
      } finally {
        this.raiseInvoiceSubmitting = false;
      }
    },
  },
};
</script>
